/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { motion } from "framer-motion";
import { FC, useEffect, useMemo, useRef, useState } from "react";

import { touchVariants } from "~/lib/motion-obj";
import { cn } from "~/lib/utils";
import BannerView from "./BannerItem";

import { useTranslation } from "react-i18next";
import { IoIosArrowRoundForward } from "react-icons/io";
import useAuthRoute from "~/hook/useAuthRoute";
import { useFontStyle } from "~/hook/useFontStyle";
import useImagePreloader from "~/hook/useImagePreloader";
import { getSession, removeSession, setSession } from "~/lib/helperFuncation/sessionStorageService";
import { AUTH_ROUTE, CATEGORY, FALLBACK_IMAGE_URL, SCROLL_STORAGE_KEY, SELECTED_ITEM } from "~/utils/constant";
import { wantsMobile } from "~/utils/helper-funcations";
import MobileIconItem from "./IconItem";
import ListView from "./ListView";
import TextItemView from "./TextItem";
import { MenuDataType, PropsType } from "./type";

const COMPONENT_MAPPING: Record<string, React.FC<PropsType>> = {
  icon: MobileIconItem,
  banner: BannerView,
  list: ListView,
  text: TextItemView,
};

const NAVIGATION = "navigation";

type MobileMenuProps = {
  categories: MenuDataType[];
  onClose?: () => void;
};

const MobileMenu: FC<MobileMenuProps> = ({ categories, onClose }) => {
  const { medium } = useFontStyle();
  const { t } = useTranslation();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { isImagePreloaded } = useImagePreloader(categories);
  const { handleNavigate } = useAuthRoute();

  const [categoryId, setCategoryId] = useState<number>(Number(getSession(CATEGORY)) || categories[0].id);
  const [prevCategoryId, setPrevCategoryId] = useState<number>(1);

  const isNavigate = useMemo(() => {
    return categories.some((category) => category.id === categoryId && category.type === NAVIGATION);
  }, [categories, categoryId]);

  const categoryName = useMemo(() => {
    const category = categories.find((category) => category.id === categoryId);
    return category?.type === NAVIGATION ? category.name : null;
  }, [categories, categoryId]);

  const handleSelectCategoryId = (category: MenuDataType) => {
    !isNavigate && handleNavigate(category.navigate_to);

    setSession(CATEGORY, category.id.toString());
    if (category.type === NAVIGATION && AUTH_ROUTE.includes(category.navigate_to)) {
      onClose && onClose();
    }
  };

  const handleSeeAllClick = (categoryId: number) => {
    const category: MenuDataType | null = categories?.find((category) => category.id === categoryId) || null;
    if (category) {
      isNavigate ? handleNavigate(category.navigate_to) : handleSelectCategoryId(category);
      onClose && onClose();
    }
  };

  const handleSelectCategory = (selectedCategory: MenuDataType) => {
    setCategoryId(selectedCategory.id);
    setSession(CATEGORY, selectedCategory.id.toString());

    if (getSession(SELECTED_ITEM)) {
      removeSession(SELECTED_ITEM);
    }
  };

  useEffect(() => {
    if (categoryId !== prevCategoryId) {
      setPrevCategoryId(categoryId);
    }
  }, [categoryId, prevCategoryId]);

  // Reset scroll when category changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      // scrollTo with behavior:"smooth" is unreliable on iOS Safari for
      // overflow containers — set scrollTop directly instead.
      scrollContainerRef.current.scrollTop = 0;
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    }
  }, [categoryId]);

  useEffect(() => {
    const savedScrollY = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    let timeout: NodeJS.Timeout;

    if (savedScrollY && scrollContainerRef.current) {
      timeout = setTimeout(() => {
        scrollContainerRef.current!.scrollTo({
          top: parseInt(savedScrollY, 10),
          behavior: "smooth",
        });
      }, 400);
    }

    const handleScroll = () => {
      if (scrollContainerRef.current) {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, scrollContainerRef.current.scrollTop.toString());
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className="flex flex-col bg-menuGradient" style={{ height: "calc(100vh - 70px)", minHeight: "calc(100vh - 70px)" }}>
      <div className="flex flex-1 min-h-0">
        {/* category */}
        <div className={cn("w-1/4 min-w-0 flex flex-col", categories?.length > 5 ? "overflow-y-auto scrollbar-hide" : "overflow-hidden")}>
          <div className="flex-1 pb-20">
            {categories.map((category, index) => {
              const isSelected = categoryId === category.id;
              const currentIndex = categories.findIndex((value) => value.id === categoryId);
              const isCurrentCategorySelected = index === currentIndex;
              const isBeforeCategorySelected = index === currentIndex - 1;
              const isAfterCategorySelected = index === currentIndex + 1;
              const isVisible = wantsMobile(category.show_menu);

              return (
                isVisible && (
                  <div
                    key={category?.id}
                    className={cn(
                      "flex flex-col items-center justify-center pt-3.5 pb-1.5 cursor-pointer border border-transparent relative",
                      isCurrentCategorySelected ? "bg-white text-black rounded-tr-lg" : "bg-secondaryColor text-textColor",
                      isAfterCategorySelected && "rounded-tr-xl",
                      isBeforeCategorySelected && "rounded-br-xl",
                    )}
                  >
                    <motion.div
                      onClick={() => handleSelectCategory(category)}
                      variants={touchVariants}
                      initial="initial"
                      whileTap="tap"
                      className={cn("rounded-md mb-2 relative", isSelected ? "bg-transparent z-40" : "bg-transparent")}
                    >
                      {isCurrentCategorySelected && (
                        <motion.div
                          layoutId="pill-move-tab"
                          transition={{ type: "spring", duration: 0.5 }}
                          className={cn("rounded-md h-[65px] w-[65px] absolute -top-2 -left-2 flex items-center justify-center bg-[#FAEFEA]  z-0")}
                        />
                      )}

                      {/* Foreground Image */}
                      <img
                        src={typeof category?.img === "string" ? category.img : FALLBACK_IMAGE_URL}
                        alt={category.name || "Category Image"}
                        width={50}
                        height={50}
                        className="relative object-contain z-10 select-none"
                        onError={(e) => {
                          (e.target as HTMLImageElement).onerror = null;
                          (e.target as HTMLImageElement).src = FALLBACK_IMAGE_URL;
                        }}
                      />
                    </motion.div>

                    <p
                      className={cn(
                        "text-[13px] w-full text-center whitespace-pre-line line-clamp-2",
                        isSelected ? "text-textHeadingColor" : "text-textColorPrimary",
                        medium,
                      )}
                      onClick={() => handleSelectCategory(category)}
                    >
                      {category.name}
                    </p>

                    {isSelected && <div className="absolute -left-0.5 top-[9px] h-[62px] w-1 bg-textHeadingColor rounded-full"></div>}
                  </div>
                )
              );
            })}

            <div
              className={cn(
                "bg-secondaryColor",
                categoryId === categories[categories?.length - 1].id && "rounded-tr-xl ",
                categories?.length > 6 ? "h-[calc(100vh-700px)]" : "h-[calc(100vh-500px)]",
              )}
            />
          </div>
        </div>
        {/* sub category */}
        <div className="flex-1  flex flex-col select-none h-[calc(100dvh-142px)] min-w-0 pt-1 relative">
          {/* if footer display block then this class apply  */}
          <div className="flex-1 overflow-y-auto screen-hight overflow-x-hidden scrollbar-hide pb-24" ref={scrollContainerRef}>
            {categoryId && (
              <div className="flex flex-col gap-y-5">
                {categories
                  ?.find((category) => category.id === categoryId)
                  ?.menu_group?.map((_category, index, arr) => {
                    const Component = COMPONENT_MAPPING[_category?.disp_type];
                    const isLast = index === arr.length - 1;
                    const isFirst = index === 0 && _category?.show_name === 1;
                    const isVisible = wantsMobile(_category.show_menu);

                    return (
                      <div key={index} className={cn(isLast ? "pb-10" : "pb-0", isFirst ? "pt-3" : "pt-0")}>
                        {_category.menu_item && isVisible ? (
                          <Component
                            shape={_category?.shape}
                            data={_category.menu_item}
                            displayItem="narrow"
                            category_name={_category?.show_name ? _category.name : null}
                            isImagePreloaded={isImagePreloaded}
                          />
                        ) : null}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {isNavigate && (
            <div className="bg-[#FFF1E0]  text-textHeadingColor border-t-2 border-t-textHeadingColor h-11 absolute -bottom-1 left-0 right-0 flex items-center py-4 b xs:w-[calc(100dvw-75px)] smd:w-[calc(100dvw-89px)] sml:w-[calc(100dvw-100px)] md:w-[calc(100dvw-470px)] md:-bottom-16">
              <div className="w-full flex items-center justify-center">
                <button
                  onClick={() => handleSeeAllClick(categoryId)}
                  className={cn("flex items-center justify-center px-3 py-2 gap-x-1", medium)}
                  type="button"
                >
                  <span className="text-14">{t("common.see_all", { type: categoryName })}</span>{" "}
                  <span>
                    <IoIosArrowRoundForward color="#d05e2d" size={20} />
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
